import { PreviewData } from '../../../../store/types/template';
import { isValidURL } from '../../../../utils/isValidUrl';
import {
  getPreviewBarcodeDimensions,
  getPreviewBarcodeImage,
} from './applePassPreview';

export const getGoogleAppLinkHref = (
  previewData: PreviewData
): string | null => {
  const {
    googleAppLinkPlayStoreUrl,
    googleAppLinkWebAppUrl,
    googleAppLinkPackageName,
  } = previewData;

  const urlsToCheck = [
    googleAppLinkPlayStoreUrl,
    googleAppLinkWebAppUrl,
    googleAppLinkPackageName &&
      `https://play.google.com/store/apps/details?id=${googleAppLinkPackageName}`,
  ];

  for (const url of urlsToCheck) {
    if (url && isValidURL(url)) {
      return url;
    }
  }

  return null;
};

export const getGoogleBackgroundColor = (previewData: PreviewData): string =>
  previewData.passDesign?.backgroundColor || previewData.passBackgroundColor;

export const getGoogleSecValuesPresent = (
  previewData: PreviewData
): boolean => {
  const {
    detailsOneId,
    detailsOneLabel,
    detailsOneValue,
    detailsTwoId,
    detailsTwoLabel,
    detailsTwoValue,
    detailsThreeId,
    detailsThreeLabel,
    detailsThreeValue,
  } = previewData;

  const detailsOne = !!detailsOneId || !!detailsOneLabel || !!detailsOneValue;
  const detailsTwo = !!detailsTwoId || !!detailsTwoLabel || !!detailsTwoValue;
  const detailsThree =
    !!detailsThreeId || !!detailsThreeLabel || !!detailsThreeValue;

  return detailsOne || detailsTwo || detailsThree;
};

export const getGoogleAuxValuesPresent = (
  previewData: PreviewData
): boolean => {
  const {
    passType,
    detailsFourId,
    detailsFourLabel,
    detailsFourValue,
    detailsFiveId,
    detailsFiveLabel,
    detailsFiveValue,
    detailsSixId,
    detailsSixLabel,
    detailsSixValue,
  } = previewData;

  const detailsFour =
    !!detailsFourId || !!detailsFourLabel || !!detailsFourValue;
  const detailsFive =
    !!detailsFiveId || !!detailsFiveLabel || !!detailsFiveValue;
  const detailsSix = !!detailsSixId || !!detailsSixLabel || !!detailsSixValue;

  if (passType === 'loyalty' || passType === 'stampCard') {
    return detailsFour;
  }

  return detailsFour || detailsFive || detailsSix;
};

const countFields = (
  ids: Array<string | undefined | null>,
  labels: Array<string | undefined | null>,
  values: Array<string | undefined | null>
): number =>
  labels.reduce((count, label, index) => {
    return count + (label || values[index] || ids[index] ? 1 : 0);
  }, 0);

export const getGoogleFirstRowCount = (previewData: PreviewData): number => {
  const {
    detailsOneId,
    detailsOneLabel,
    detailsOneValue,
    detailsTwoId,
    detailsTwoLabel,
    detailsTwoValue,
    detailsThreeId,
    detailsThreeLabel,
    detailsThreeValue,
  } = previewData;

  return countFields(
    [detailsOneId, detailsTwoId, detailsThreeId],
    [detailsOneLabel, detailsTwoLabel, detailsThreeLabel],
    [detailsOneValue, detailsTwoValue, detailsThreeValue]
  );
};

export const getGoogleSecondRowCount = (previewData: PreviewData): number => {
  const {
    detailsFourId,
    detailsFourLabel,
    detailsFourValue,
    detailsFiveId,
    detailsFiveLabel,
    detailsFiveValue,
    detailsSixId,
    detailsSixLabel,
    detailsSixValue,
  } = previewData;

  return countFields(
    [detailsFourId, detailsFiveId, detailsSixId],
    [detailsFourLabel, detailsFiveLabel, detailsSixLabel],
    [detailsFourValue, detailsFiveValue, detailsSixValue]
  );
};

export const getGoogleRowAlignments = (
  count: number,
  ids: Array<string | undefined | null>,
  labels: Array<string | undefined | null>,
  values: Array<string | undefined | null>
): {
  secondAlignment: 'left' | 'center' | 'right';
  thirdAlignment: 'left' | 'center' | 'right';
} => {
  const firstFieldEmpty = !ids[0] && !labels[0] && !values[0];
  const secondFieldEmpty = !ids[1] && !labels[1] && !values[1];

  if (count === 1) {
    return { secondAlignment: 'left', thirdAlignment: 'left' };
  } else if (count === 2) {
    return {
      secondAlignment: firstFieldEmpty ? 'left' : 'right',
      thirdAlignment: firstFieldEmpty && secondFieldEmpty ? 'left' : 'right',
    };
  } else if (count === 3) {
    return {
      secondAlignment: firstFieldEmpty ? 'left' : 'center',
      thirdAlignment:
        !firstFieldEmpty && !secondFieldEmpty ? 'right' : 'center',
    };
  } else if (count === 4) {
    return { secondAlignment: 'center', thirdAlignment: 'center' };
  }

  return { secondAlignment: 'left', thirdAlignment: 'left' };
};

export const getBarcodePreviewConfig = (previewData: PreviewData) => {
  const type = previewData?.barcode?.type;
  const image = getPreviewBarcodeImage(type);
  const { width, height } = getPreviewBarcodeDimensions(type);

  return { image, width, height };
};

export const getGoogleWatermarkConfig = (previewData: PreviewData) => {
  if (previewData.googleBannerImage) {
    return {
      color: previewData.googleBannerWatermarkColor,
      position: previewData.googleBannerWatermarkPosition,
    };
  }

  return {
    color: previewData.watermarkColor,
    position: previewData.watermarkPosition,
  };
};

export const isDuplicateBackField = (
  previewData: PreviewData,
  fieldPrefix: string
): boolean => {
  const id = previewData[`${fieldPrefix}Id` as keyof PreviewData];
  if (!id) return false;

  const backFields = ['One', 'Two', 'Three', 'Four', 'Five'];

  return backFields.some((suffix) => {
    const backId = previewData[`back${suffix}Id` as keyof PreviewData];
    return backId === id;
  });
};
